import type { PropsWithChildren } from "react";

import { styled } from "@linaria/react";
import { match } from "ts-pattern";

import { withTheme } from "../theme/GalaxyTheme";
import type { PropsWithTheme } from "../theme/types";

import { getCssSizeValue } from "../utils/linaria";

export enum WidgetVariant {
  BASE = "BASE",
  PRIMARY = "PRIMARY",
  SECONDARY = "SECONDARY",
  TERTIARY = "TERTIARY",
  ERROR = "ERROR",
  DISABLED = "DISABLED",
}

interface WidgetProps {
  header?: string;
  minWidth?: number | string;
  maxWidth?: number | string;
  minHeight?: number | string;
  maxHeight?: number | string;
  fillWidth?: boolean;
  fillHeight?: boolean;
  padding?: React.CSSProperties["padding"];
  noPadding?: boolean;
  noHover?: boolean;
  noBorder?: boolean;
  isGradient?: boolean;
  isSelected?: boolean;
  variant?: WidgetVariant;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const StyledWidget = withTheme(styled.div<
  PropsWithTheme<{
    $minWidth?: number | string;
    $maxWidth?: number | string;
    $minHeight?: number | string;
    $maxHeight?: number | string;
    $fillWidth?: boolean;
    $fillHeight?: boolean;
    $padding?: React.CSSProperties["padding"];
    $noPadding?: boolean;
    $noHover?: boolean;
    $noBorder?: boolean;
    $isGradient?: boolean;
    $isSelected?: boolean;
    $variant: WidgetVariant;
    $isClickable?: boolean;
  }>
>`
  background: ${({ theme, $variant }) => {
    return match($variant)
      .with(WidgetVariant.BASE, () => theme.color.background.base)
      .with(WidgetVariant.PRIMARY, () => theme.color.background.primary)
      .with(WidgetVariant.SECONDARY, () => theme.color.background.secondary)
      .with(WidgetVariant.TERTIARY, () => theme.color.background.tertiary)
      .with(WidgetVariant.ERROR, () => theme.color.background.primary)
      .with(WidgetVariant.DISABLED, () => theme.color.background.disabled)
      .exhaustive();
  }};
  border: 0.5px solid
    ${({ theme, $noBorder, $isSelected, $variant }) => {
      if ($noBorder) return "transparent";
      if ($isSelected) return theme.color.border.selected;
      return match($variant)
        .with(WidgetVariant.BASE, () => theme.color.border.primary)
        .with(WidgetVariant.PRIMARY, () => theme.color.border.primary)
        .with(WidgetVariant.SECONDARY, () => theme.color.border.primary)
        .with(WidgetVariant.TERTIARY, () => theme.color.border.primary)
        .with(WidgetVariant.ERROR, () => theme.color.border.error)
        .with(WidgetVariant.DISABLED, () => theme.color.border.disabled)
        .exhaustive();
    }};
  border-radius: 5px;
  padding: ${({ $padding, $noPadding }) => $padding ?? ($noPadding ? "0" : "12px")};
  box-shadow:
    0 1px 10px 0 ${({ theme }) => theme.color.opacity.bw16},
    0 1px 1px 0 ${({ theme }) => theme.color.opacity.bw12};
  width: ${({ $fillWidth }) => ($fillWidth ? "100%" : "auto")};
  height: ${({ $fillHeight }) => ($fillHeight ? "100%" : "auto")};
  min-width: ${({ $minWidth }) => {
    if ($minWidth === undefined) {
      return "auto";
    }
    return getCssSizeValue($minWidth);
  }};
  max-width: ${({ $maxWidth }) => {
    if ($maxWidth === undefined) {
      return "auto";
    }
    return getCssSizeValue($maxWidth);
  }};
  min-height: ${({ $minHeight }) => {
    if ($minHeight === undefined) {
      return "auto";
    }
    return getCssSizeValue($minHeight);
  }};
  max-height: ${({ $maxHeight }) => {
    if ($maxHeight === undefined) {
      return "auto";
    }
    return getCssSizeValue($maxHeight);
  }};

  cursor: ${({ $isClickable, $variant }) => {
    if ($variant === WidgetVariant.ERROR) return "not-allowed";
    if ($isClickable) return "pointer";
    return "inherit";
  }};

  &:hover {
    border-color: ${({ theme, $noHover, $isSelected, $isClickable, $variant }) => {
      // Only apply hover effect if widget is clickable
      if (!$isClickable) {
        // Return same border color as non-hover state (no visual change)
        return match($variant)
          .with(WidgetVariant.ERROR, () => theme.color.border.error)
          .with(WidgetVariant.DISABLED, () => theme.color.border.disabled)
          .otherwise(() => theme.color.border.primary);
      }

      // Clickable widget hover states
      return match($variant)
        .with(WidgetVariant.ERROR, () => theme.color.border.error)
        .with(WidgetVariant.DISABLED, () => theme.color.border.disabled)
        .otherwise(() => {
          if ($isSelected) return theme.color.border.selected;
          if ($noHover) return theme.color.border.primary;
          return theme.color.border.secondary;
        });
    }};
  }
`);

const Widget = ({
  children,
  minHeight,
  maxHeight,
  minWidth,
  maxWidth,
  fillWidth,
  fillHeight,
  padding,
  noPadding,
  noHover,
  noBorder,
  isSelected,
  variant = WidgetVariant.PRIMARY,
  onClick,
}: PropsWithChildren<WidgetProps>) => {
  return (
    <StyledWidget
      $minWidth={minWidth}
      $maxWidth={maxWidth}
      $minHeight={minHeight}
      $maxHeight={maxHeight}
      $fillWidth={fillWidth}
      $fillHeight={fillHeight}
      $padding={padding}
      $noPadding={noPadding}
      $noHover={noHover}
      $noBorder={noBorder}
      $isSelected={isSelected}
      $variant={variant}
      $isClickable={!!onClick}
      onClick={onClick}
    >
      {children}
    </StyledWidget>
  );
};

export default Widget;
